// src/services/cart.service.js

import PricingService from './pricing.service';
import TaxService from './tax.service';
import DiscountService from './discount.service';
import { useMainStore } from '@/stores/mainStore';
/**
 * Service de gestion du panier
 * Responsable du calcul et de l'agrégation des prix
 */
class CartService {
    constructor() {
        this.pricingService = PricingService;
        this.taxService = TaxService;
        this.discountService = DiscountService;
        this._cache = new Map();
        this._cacheTimeout = 1000; // 1 seconde de cache
    }

    /**
     * Calcule le panier complet pour une réservation
     * 
     * @param {Object} booking - La réservation complète
     * @param {Object} options - Options de calcul
     * @param {boolean} options.includeTax - Inclure les taxes
     * @param {boolean} options.useCache - Utiliser le cache
     * @returns {Promise<Object>} Le panier calculé
     */
    async computeCart(booking, options = {}) {
        const {
            includeTax = true,
            useCache = true
        } = options;



        // Vérifier le cache
        const cacheKey = this._generateCacheKey(booking);
        if (useCache && this._cache.has(cacheKey)) {
            const cached = this._cache.get(cacheKey);
            if (Date.now() - cached.timestamp < this._cacheTimeout) {
                return cached.data;
            }
        }

        try {
            // Calculer le panier de base
            const baseCart = await this._computeBaseCart(booking);

            // Appliquer les réductions (codes de réduction)
            const cartWithDiscounts = await this._applyDiscounts(baseCart, booking);

            // Appliquer la remise initiale d'inscription si présente
            const cartWithInitialDiscount = this._applyInitialInscriptionDiscount(cartWithDiscounts, booking);

            // Calculer les taxes si nécessaire

            const finalCart = includeTax
                ? await this._applyTaxes(cartWithInitialDiscount, booking)
                : await this._dontApplyTaxes(cartWithInitialDiscount);



            // Mettre en cache
            if (useCache) {
                this._cache.set(cacheKey, {
                    data: finalCart,
                    timestamp: Date.now()
                });
            }

            return this._formatCart(finalCart);
        } catch (error) {
            console.error('Erreur lors du calcul du panier:', error);
            throw new Error('Impossible de calculer le panier');
        }
    }

    /**
     * Calcule le panier de base (sans réductions ni taxes)
     * 
     * @private
     * @param {Object} booking - La réservation
     * @returns {Promise<Object>} Le panier de base
     */
    async _computeBaseCart(booking) {
        const cart = {
            items: [],
            subtotal: 0,
            totalItems: 0,
            breakdown: {
                courses: { prix: 0, nb_articles: 0 },
                packs_courses: { prix: 0, nb_articles: 0 },
                options: { prix: 0, nb_articles: 0 },
                activites: { prix: 0, nb_articles: 0 },
                articles: { prix: 0, nb_articles: 0 },
                gia: { prix: 0, nb_articles: 0 },
                donation: { prix: 0, nb_articles: 0 },
                alerte_sms: { prix: 0, nb_articles: 0 },
                frais_bourse_dossard: { prix: 0, nb_articles: 0 }
            }
        };

        // Parcourir toutes les inscriptions
        for (const inscription of booking.inscriptions || []) {
            const inscriptionCart = await this._computeInscriptionCart(inscription, booking);

            // Agréger les résultats
            cart.subtotal += inscriptionCart.prix;
            cart.totalItems += inscriptionCart.nb_articles;

            // Agréger par type
            for (const [type, value] of Object.entries(inscriptionCart.type || {})) {
                if (cart.breakdown[type]) {
                    cart.breakdown[type].prix += parseFloat(value.prix || 0);
                    cart.breakdown[type].nb_articles += parseInt(value.nb_articles || 0);
                }
            }

            cart.items.push({
                inscriptionIndex: booking.inscriptions.indexOf(inscription),
                cart: inscriptionCart
            });
        }

        return cart;
    }

    /**
     * Calcule le panier pour une inscription individuelle
     * 
     * @private
     * @param {Object} inscription - L'inscription
     * @param {Object} booking - La réservation complète
     * @returns {Promise<Object>} Le panier de l'inscription
     */
    async _computeInscriptionCart(inscription, booking) {
        const cart = {
            prix: 0,
            nb_articles: 0,
            type: {}
        };

        // Calculer chaque type d'élément
        const calculations = [
            this._computeCoursesCart(inscription, booking),
            this._computeOptionsCart(inscription, booking),
            this._computeActivitesCart(inscription, booking),
            this._computePacksCart(inscription),
            this._computeArticlesCart(inscription),
            this._computeGIACart(inscription),
            this._computeDonationCart(inscription),
            this._computeAlerteSMSCart(inscription),
            this._computeFraisBourseDossardCart(inscription)
        ];

        const results = await Promise.all(calculations);

        // Agréger les résultats
        for (const result of results) {
            if (result) {
                cart.type[result.type] = result.cart;
                cart.prix += parseFloat(result.cart.prix || 0);
                cart.nb_articles += parseInt(result.cart.nb_articles || 0);
            }
        }

        return cart;
    }

    /**
     * Calcule le panier pour les courses
     * 
     * @private
     * @param {Object} inscription - L'inscription
     * @param {Object} booking - La réservation
     * @returns {Promise<Object>} Le résultat du calcul
     */
    async _computeCoursesCart(inscription) {
        if (!inscription.list_courses) {
            return null;
        }
        // charger mainstore inscription_vip et groupe
        const mainStore = useMainStore();
        const vipIns = mainStore.inscription_vip;
        const groupe = mainStore.bookingSystem?.groupe.$state;

        // Utiliser le PricingService pour calculer les prix
        const cart = await this.pricingService.computeCoursesPrice(
            inscription.list_courses,
            inscription.list_packs_courses,
            inscription.coureur,
            vipIns,
            inscription,
            groupe
        );

        return {
            type: 'courses',
            cart: cart || { prix: 0, nb_articles: 0 }
        };
    }

    /**
     * Calcule le panier pour les packs
     * 
     * @private
     */
    async _computePacksCart(inscription) {
        if (!inscription.list_packs_courses) {
            return null;
        }
        const mainStore = useMainStore();
        const groupe = mainStore.bookingSystem?.groupe.$state;

        const cart = await this.pricingService.computePackCoursesPrice(inscription.list_packs_courses, groupe);

        return {
            type: 'packs_courses',
            cart: cart || { prix: 0, nb_articles: 0 }
        };
    }

    /**
     * Calcule le panier pour les options
     * 
     * @private
     */
    async _computeOptionsCart(inscription, booking) {
        if (!inscription.list_options) {
            return null;
        }

        const mainStore = useMainStore();
        const vipIns = booking.vip_ins;
        const groupe = mainStore.bookingSystem?.groupe?.$state;
        const cart = await this.pricingService.computeOptionsPrice(inscription.list_options, vipIns, groupe);

        return {
            type: 'options',
            cart: cart || { prix: 0, nb_articles: 0 }
        };
    }

    /**
     * Calcule le panier pour les activités
     * 
     * @private
     */
    async _computeActivitesCart(inscription, booking) {
        if (!inscription.list_activites) {
            return null;
        }

        const mainStore = useMainStore();
        const vipIns = booking.vip_ins;
        const groupe = mainStore.bookingSystem?.groupe?.$state;
        const cart = await this.pricingService.computeActivitesPrice(inscription.list_activites, vipIns, groupe);

        return {
            type: 'activites',
            cart: cart || { prix: 0, nb_articles: 0 }
        };
    }

    /**
     * Calcule le panier pour les articles
     * 
     * @private
     */
    async _computeArticlesCart(inscription) {
        if (!inscription.list_articles) {
            return null;
        }

        const mainStore = useMainStore();
        const groupe = mainStore.bookingSystem?.groupe?.$state;
        const cart = await this.pricingService.computeArticlesPrice(inscription.list_articles, groupe);

        return {
            type: 'articles',
            cart: cart || { prix: 0, nb_articles: 0 }
        };
    }

    /**
     * Calcule le panier pour la GIA
     * 
     * @private
     */
    async _computeGIACart(inscription) {
        if (!inscription.gia) {
            return null;
        }

        const cart = await this.pricingService.computeGIAPrice(inscription.gia);

        return {
            type: 'gia',
            cart: cart || { prix: 0, nb_articles: 0 }
        };
    }

    /**
     * Calcule le panier pour les donations
     * 
     * @private
     */
    async _computeDonationCart(inscription) {
        if (!inscription.donation) {
            return null;
        }

        const cart = await this.pricingService.computeDonationPrice(inscription.donation);

        return {
            type: 'donation',
            cart: cart || { prix: 0, nb_articles: 0 }
        };
    }

    /**
     * Calcule le panier pour l'alerte SMS
     * 
     * @private
     */
    async _computeAlerteSMSCart(inscription) {
        if (!inscription.alerte_sms) {
            return null;
        }

        const cart = await this.pricingService.computeAlerteSMSPrice(inscription.alerte_sms);

        return {
            type: 'alerte_sms',
            cart: cart || { prix: 0, nb_articles: 0 }
        };
    }

    /**
     * Calcule le panier pour les frais de bourse dossard
     * 
     * @private
     */
    async _computeFraisBourseDossardCart(inscription) {
        if (!inscription.frais_bourse_dossard) {
            return null;
        }

        return {
            type: 'frais_bourse_dossard',
            cart: inscription.frais_bourse_dossard
        };
    }

    /**
     * Construit le breakdown pour une inscription spécifique
     * 
     * @private
     * @param {Object} inscriptionCart - Le panier de l'inscription
     * @returns {Object} Le breakdown au format attendu par computeDiscount
     */
    _buildInscriptionBreakdown(inscriptionCart) {
        const breakdown = {
            courses: { prix: 0, nb_articles: 0 },
            packs_courses: { prix: 0, nb_articles: 0 },
            options: { prix: 0, nb_articles: 0 },
            activites: { prix: 0, nb_articles: 0 },
            articles: { prix: 0, nb_articles: 0 },
            gia: { prix: 0, nb_articles: 0 },
            donation: { prix: 0, nb_articles: 0 },
            alerte_sms: { prix: 0, nb_articles: 0 },
            frais_bourse_dossard: { prix: 0, nb_articles: 0 }
        };

        // Extraire les valeurs du type de l'inscription
        if (inscriptionCart.type) {
            for (const [type, value] of Object.entries(inscriptionCart.type)) {
                if (breakdown[type] && value) {
                    breakdown[type] = {
                        prix: parseFloat(value.prix || 0),
                        nb_articles: parseInt(value.nb_articles || 0)
                    };
                }
            }
        }

        return breakdown;
    }

    /**
     * Applique les réductions au panier
     * 
     * @private
     * @param {Object} cart - Le panier de base
     * @param {Object} booking - La réservation
     * @returns {Promise<Object>} Le panier avec réductions
     */
    async _applyDiscounts(cart, booking) {
        let discountTotal = 0;
        const discounts = [];

        // Parcourir toutes les inscriptions pour trouver les codes de réduction
        for (const inscription of booking.inscriptions || []) {

            if (inscription.code_reduction) {


                // Récupérer le breakdown de l'inscription spécifique
                const inscriptionIndex = booking.inscriptions.indexOf(inscription);
                const inscriptionItem = cart.items.find(item => item.inscriptionIndex === inscriptionIndex);

                if (!inscriptionItem || !inscriptionItem.cart) {
                    continue;
                }

                // Construire le breakdown pour cette inscription uniquement
                const inscriptionBreakdown = this._buildInscriptionBreakdown(inscriptionItem.cart);

                const discount = await this.discountService.computeDiscount(
                    inscription.code_reduction,
                    inscriptionBreakdown,
                    inscription
                );

                if (discount && discount.prix < 0) {
                    discountTotal += Math.abs(discount.prix);
                    discounts.push({
                        code: inscription.code_reduction.nom,
                        amount: Math.abs(discount.prix),
                        inscriptionIndex: inscriptionIndex
                    });
                }
            }
        }

        return {
            ...cart,
            discount: {
                total: parseFloat(discountTotal.toFixed(2)),
                items: discounts
            },
            subtotalAfterDiscount: parseFloat((cart.subtotal - discountTotal).toFixed(2))
        };
    }

    /**
     * Applique la remise initiale d'inscription si présente
     * 
     * @private
     * @param {Object} cart - Le panier avec réductions de codes
     * @param {Object} booking - La réservation
     * @returns {Object} Le panier avec remise initiale appliquée
     */
    _applyInitialInscriptionDiscount(cart, booking) {
        // Vérifier si une remise initiale d'inscription existe
        if (!booking.initial_commande_inscription || !booking.initial_commande_inscription.prix) {
            return cart;
        }

        const initialDiscount = parseFloat(booking.initial_commande_inscription.prix);

        // Si le montant de la remise initiale est valide et positif
        if (isNaN(initialDiscount) || initialDiscount <= 0) {
            return cart;
        }

        // Calculer le nouveau subtotal après remise initiale
        const currentSubtotal = cart.subtotalAfterDiscount || cart.subtotal;
        const newSubtotal = Math.max(0, currentSubtotal - initialDiscount);

        return {
            ...cart,
            initialInscriptionDiscount: {
                amount: parseFloat(initialDiscount.toFixed(2)),
                description: 'Remise initiale d\'inscription'
            },
            subtotalAfterDiscount: parseFloat(newSubtotal.toFixed(2))
        };
    }

    /**
     * Applique les taxes au panier
     * 
     * @private
     * @param {Object} cart - Le panier avec réductions
     * @param {Object} booking - La réservation
     * @returns {Promise<Object>} Le panier avec taxes
     */
    async _applyTaxes(cart, booking) {
        let total = cart.subtotal;
        if (cart.subtotalAfterDiscount !== undefined && cart.subtotalAfterDiscount !== null) {
            total = cart.subtotalAfterDiscount;
        }
        if (booking.exclude_taxe) {
            return {
                ...cart,
                tax: 0,
                total: total
            };
        }

        const tax = await this.taxService.computeTax(
            total,
            booking.id_module,
            booking
        );

        return {
            ...cart,
            tax: parseFloat(tax.toFixed(2)),
            total: parseFloat((cart.subtotalAfterDiscount + tax).toFixed(2))
        };
    }

    async _dontApplyTaxes(cart) {
        let total = cart.subtotal;

        if (cart.subtotalAfterDiscount !== undefined && cart.subtotalAfterDiscount !== null) {
            total = cart.subtotalAfterDiscount;
        }
        return {
            ...cart,
            tax: 0,
            total: total
        };
    }

    /**
     * Formate le panier pour l'affichage
     * 
     * @private
     * @param {Object} cart - Le panier calculé
     * @returns {Object} Le panier formaté
     */
    _formatCart(cart) {
        // Calculer le total des réductions (codes + remise initiale)
        const codeDiscountsTotal = cart.discount?.total || 0;
        const initialDiscountAmount = cart.initialInscriptionDiscount?.amount || 0;
        const totalDiscounts = codeDiscountsTotal + initialDiscountAmount;

        // Construire la liste complète des réductions
        const allDiscounts = [...(cart.discount?.items || [])];
        if (initialDiscountAmount > 0) {
            allDiscounts.push({
                code: 'Remise initiale',
                amount: initialDiscountAmount,
                type: 'initial_inscription'
            });
        }

        let subtotal = cart.subtotal;
        if (cart.subtotalAfterDiscount !== undefined && cart.subtotalAfterDiscount !== null) {
            subtotal = cart.subtotalAfterDiscount;
        }

        return {
            prix: subtotal,
            prix_total: cart.total,
            nb_articles: cart.totalItems,
            taxe: cart.tax || 0,
            breakdown: cart.breakdown,
            discounts: {
                total: parseFloat(totalDiscounts.toFixed(2)),
                items: allDiscounts
            },
            initialInscriptionDiscount: cart.initialInscriptionDiscount || null,
            items: cart.items
        };
    }

    /**
     * Génère une clé de cache unique pour une réservation
     * 
     * @private
     * @param {Object} booking - La réservation
     * @returns {string} La clé de cache
     */
    _generateCacheKey(booking) {
        // Créer une empreinte simple de la réservation
        const key = JSON.stringify({
            id_module: booking.id_module,
            nb_inscriptions: booking.inscriptions?.length || 0,
            uuid: booking.uuid,
            // Inclure les IDs des courses sélectionnées
            courses: booking.inscriptions?.map(ins =>
                ins.list_courses?.list?.filter(c => c.selected).map(c => c.id)
            ).flat() || []
        });
        return `cart_${this._hashCode(key)}`;
    }

    /**
     * Génère un hash simple d'une chaîne
     * 
     * @private
     * @param {string} str - La chaîne à hasher
     * @returns {number} Le hash
     */
    _hashCode(str) {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32bit integer
        }
        return hash;
    }

    /**
     * Vide le cache
     */
    clearCache() {
        this._cache.clear();
    }

    /**
     * Invalide le cache pour une réservation spécifique
     * 
     * @param {Object} booking - La réservation
     */
    invalidateCache(booking) {
        const cacheKey = this._generateCacheKey(booking);
        this._cache.delete(cacheKey);
    }
}

export default new CartService();